<template>
  <div v-if="visible" class="md-sheet">
    <div class="md-sheet__backdrop" @click="$emit('close')"></div>
    <div class="md-sheet__panel">
      <div class="md-sheet__grip"></div>
      <div class="md-sheet__head">
        <h3 class="md-sheet__title">{{ $t('mobileDash.filters') }}</h3>
        <button type="button" class="md-sheet__close" @click="$emit('close')">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="md-sheet__body">
        <!-- Date range -->
        <div class="md-sheet__group">
          <label class="md-sheet__label">{{ $t('mobileDash.dateRange') }}</label>
          <div class="md-sheet__presets">
            <button
                v-for="p in presets" :key="p"
                type="button"
                class="md-sheet__preset"
                :class="{ 'is-active': draft.preset === p }"
                @click="selectPreset(p)"
            >
              {{ p === 'custom' ? $t('mobileDash.customRange') : $t(`filters.picker.${p}`) }}
            </button>
          </div>
          <!-- b-form-datepicker: real tap-to-open calendar on every browser
               (native type=date inputs only allowed manual typing on some) -->
          <div v-if="draft.preset === 'custom'" class="md-sheet__dates">
            <b-form-datepicker
                v-model="draft.range.from"
                size="sm"
                :max="draft.range.until || null"
                :locale="$i18n.locale"
                hide-header
                :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            />
            <span class="md-sheet__dates-sep">–</span>
            <b-form-datepicker
                v-model="draft.range.until"
                size="sm"
                :min="draft.range.from || null"
                :locale="$i18n.locale"
                hide-header
                :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            />
          </div>
        </div>

        <!-- Department -->
        <div class="md-sheet__group">
          <label class="md-sheet__label">{{ $t('mobileDash.department') }}</label>
          <div class="md-sheet__seg">
            <button
                v-for="d in departments" :key="d.key || 'all'"
                type="button"
                class="md-sheet__seg-btn"
                :class="{ 'is-active': draft.department === d.key }"
                @click="draft.department = d.key"
            >
              {{ d.label }}
            </button>
          </div>
        </div>

        <!-- Desk -->
        <div v-if="deskOptions.length > 1" class="md-sheet__group">
          <label class="md-sheet__label">{{ $t('mobileDash.desks') }}</label>
          <v-select
              v-model="draft.desks"
              :options="deskOptions"
              multiple
              :placeholder="$t('mobileDash.allDesks')"
          />
        </div>

        <!-- Organization -->
        <div v-if="hasManyOrganizations" class="md-sheet__group">
          <label class="md-sheet__label">{{ $t('mobileDash.organizations') }}</label>
          <organization-select-box v-model="draft.orgIds" multiple clearable
                                   :placeholder="$t('mobileDash.allOrganizations')"/>
        </div>

        <!-- Business unit -->
        <div v-if="hasManyBusinessUnits" class="md-sheet__group">
          <label class="md-sheet__label">{{ $t('mobileDash.businessUnits') }}</label>
          <business-unit-select-box v-model="draft.buIds" multiple clearable
                                    :selected-organization="draft.orgIds"
                                    :selected-department="draft.department ? [draft.department] : []"
                                    :placeholder="$t('mobileDash.allBusinessUnits')"/>
        </div>
      </div>

      <div class="md-sheet__foot">
        <b-button variant="outline-secondary" class="md-sheet__reset" @click="reset">
          {{ $t('mobileDash.reset') }}
        </b-button>
        <b-button variant="primary" class="md-sheet__apply" @click="apply">
          {{ $t('mobileDash.apply') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { mapGetters } from 'vuex';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox';
import { sortDesks } from '@/model/Utils';

export const PRESETS = ['today', 'yesterday', 'thisWeek', 'thisMonth', 'lastMonth', 'custom'];

export function presetRange(preset, $moment) {
  switch (preset) {
    case 'today':
      return { from: $moment().format('YYYY-MM-DD'), until: $moment().format('YYYY-MM-DD') };
    case 'yesterday':
      return {
        from: $moment().subtract(1, 'days').format('YYYY-MM-DD'),
        until: $moment().subtract(1, 'days').format('YYYY-MM-DD')
      };
    case 'thisWeek':
      return {
        from: $moment().startOf('week').format('YYYY-MM-DD'),
        until: $moment().endOf('week').format('YYYY-MM-DD')
      };
    case 'lastMonth':
      return {
        from: $moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        until: $moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      };
    case 'thisMonth':
    default:
      return {
        from: $moment().startOf('month').format('YYYY-MM-DD'),
        until: $moment().endOf('month').format('YYYY-MM-DD')
      };
  }
}

export function defaultFilter($moment) {
  return {
    preset: 'thisMonth',
    range: presetRange('thisMonth', $moment),
    orgIds: [],
    buIds: [],
    desks: [],
    department: null,
  };
}

export default {
  name: 'MdFilterSheet',
  components: { OrganizationSelectBox, BusinessUnitSelectBox, vSelect },
  props: {
    visible: { type: Boolean, default: false },
    value: { type: Object, required: true },
  },
  data() {
    return {
      presets: PRESETS,
      draft: defaultFilter(this.$moment),
    };
  },
  computed: {
    ...mapGetters('data', ['allOrganizations', 'hasManyOrganizations', 'hasManyBusinessUnits']),
    departments() {
      return [
        { key: null, label: this.$t('mobileDash.allDepartments') },
        { key: 'SALES', label: this.$t('mobileDash.sales') },
        { key: 'RETENTION', label: this.$t('mobileDash.retention') },
      ];
    },
    deskOptions() {
      const desks = new Set();
      (this.allOrganizations || []).forEach(org => desks.add(org.desk && org.desk.length ? org.desk : 'No Desk'));
      return sortDesks([...desks]);
    },
  },
  watch: {
    visible(open) {
      if (open) {
        // Work on a deep copy so Cancel/backdrop discards changes
        this.draft = JSON.parse(JSON.stringify({ ...defaultFilter(this.$moment), ...this.value }));
      }
      document.body.style.overflow = open ? 'hidden' : '';
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    selectPreset(preset) {
      this.draft.preset = preset;
      if (preset !== 'custom') {
        this.draft.range = presetRange(preset, this.$moment);
      }
    },
    reset() {
      this.draft = defaultFilter(this.$moment);
    },
    apply() {
      this.$emit('input', JSON.parse(JSON.stringify(this.draft)));
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.md-sheet__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 20, 26, 0.5);
  z-index: 1090;
  animation: md-fade 0.2s ease;
}
.md-sheet__panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1091;
  background: var(--md-card-bg, #fff);
  border-radius: 16px 16px 0 0;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  animation: md-slide-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  padding-bottom: env(safe-area-inset-bottom, 0);
}
.md-sheet__grip {
  width: 38px;
  height: 4px;
  border-radius: 999px;
  background: rgba(138, 145, 156, 0.35);
  margin: 8px auto 0;
}
.md-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1rem 0.4rem;
}
.md-sheet__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.md-sheet__close {
  border: none;
  background: rgba(138, 145, 156, 0.12);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  font-size: 14px;
  color: var(--md-muted, #8a919c);
}
.md-sheet__body {
  overflow-y: auto;
  padding: 0.25rem 1rem 1rem;
  -webkit-overflow-scrolling: touch;
}
.md-sheet__group { margin-bottom: 1rem; }
.md-sheet__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--md-muted, #8a919c);
  margin-bottom: 0.4rem;
}
.md-sheet__presets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
}
.md-sheet__preset {
  border: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.12));
  background: transparent;
  border-radius: 8px;
  padding: 0.5rem 0.25rem;
  font-size: 12px;
  font-weight: 600;
  color: var(--md-strong, #24292f);
}
.md-sheet__preset.is-active {
  border-color: var(--primary, #7367f0);
  color: var(--primary, #7367f0);
  background: rgba(115, 103, 240, 0.08);
}
.md-sheet__dates {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.md-sheet__dates ::v-deep .b-form-datepicker {
  flex: 1;
  min-width: 0;
}
.md-sheet__dates-sep { color: var(--md-muted, #8a919c); }
.md-sheet__seg {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  padding: 3px;
  background: rgba(138, 145, 156, 0.1);
  border-radius: 9px;
}
.md-sheet__seg-btn {
  border: none;
  background: transparent;
  border-radius: 7px;
  padding: 0.45rem 0.25rem;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--md-muted, #8a919c);
}
.md-sheet__seg-btn.is-active {
  background: var(--md-card-bg, #fff);
  color: var(--md-strong, #24292f);
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.14);
}
.md-sheet__foot {
  display: flex;
  gap: 0.6rem;
  padding: 0.7rem 1rem;
  border-top: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
}
.md-sheet__reset { flex: 1; }
.md-sheet__apply { flex: 2; }

@keyframes md-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes md-slide-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.dark-layout .md-sheet__panel {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
</style>
